import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import { useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse } from '../../../util/urlHelpers';
import { createSlug } from '../../../util/urlHelpers';
import { createResourceLocatorString } from '../../../util/routes';

import { Form, IconClose, LocationAutocompleteInput } from '../../../components';

import IconSearchDesktop from '../../TopbarContainer/Topbar/TopbarSearchForm/IconSearchDesktop';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';

import { searchKeywordSuggestions } from './SearchPageSearchBar.duck';

import css from './SearchPageSearchBar.module.css';
import dropdownCss from './SearchPageSearchBarDropdown.module.css';

const identity = v => v;

// How long to wait after the user stops typing before we ask the API for
// matching listings. Keeps us from firing a request on every keystroke.
const SUGGESTION_DEBOUNCE_MS = 300;

// Same initial values as the topbar search form (see Topbar.js), read from the URL.
const getInitialValues = (config, search) => {
  const { keywords, address, origin, bounds } = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  if (isMainSearchTypeKeywords(config)) {
    return { keywords };
  }

  // Only render current search if full place object is available in the URL params
  const locationFieldsPresent = isOriginInUse(config)
    ? address && origin && bounds
    : address && bounds;
  return {
    location: locationFieldsPresent
      ? { search: address, selectedPlace: { address, origin, bounds } }
      : null,
  };
};

// Same search params as the topbar search (see handleSubmit in Topbar.js).
const getSearchParams = (config, values) => {
  if (isMainSearchTypeKeywords(config)) {
    return { keywords: values?.keywords };
  }
  // search defaults to 'location' search
  const { search, selectedPlace } = values?.location || {};
  const { origin, bounds } = selectedPlace || {};
  const originMaybe = isOriginInUse(config) ? { origin } : {};

  return {
    ...originMaybe,
    address: search,
    bounds,
  };
};

/**
 * Search input shown above the search results on mobile and tablet, where the topbar
 * has no search of its own. It searches exactly like the topbar search does: the main
 * search type (keywords or location) comes from the marketplace configuration, and the
 * current filters are kept.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {Object} props.location - The router location
 * @param {Object} props.history - The router history
 * @param {Array} props.routeConfiguration - The route configuration
 * @param {Object} props.config - The marketplace configuration
 * @param {Object} props.currentSearchParams - The current (valid) search query params
 * @returns {JSX.Element}
 */
const SearchPageSearchBar = props => {
  const intl = useIntl();
  const inputRef = useRef(null);
  const { className, location, history, routeConfiguration, config, currentSearchParams } = props;

  const dispatch = useDispatch();

  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const debounceTimeout = useRef(null);
  const wrapperRef = useRef(null);
  const latestQueryRef = useRef('');

  // Close the dropdown when the user taps/clicks anywhere outside of it.
  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean up any pending debounce timer if the component unmounts mid-type.
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const fetchSuggestions = value => {
    const trimmed = value ? value.trim() : '';
    latestQueryRef.current = trimmed;

    if (trimmed.length === 0) {
      setSuggestions([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsOpen(true);

    dispatch(searchKeywordSuggestions({ keywords: trimmed }))
      .unwrap()
      .then(listings => {
        // Ignore results for a query that's no longer the latest one typed
        // (e.g. a slow request for "vio" resolving after "violin" was typed).
        if (latestQueryRef.current === trimmed) {
          setSuggestions(listings || []);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (latestQueryRef.current === trimmed) {
          setSuggestions([]);
          setIsLoading(false);
        }
      });
  };

  const handleKeywordsChange = (value, originalOnChange) => {
    originalOnChange(value);

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      fetchSuggestions(value);
    }, SUGGESTION_DEBOUNCE_MS);
  };

  const handleSelectListing = listing => {
    const id = listing.id.uuid;
    const title = listing.attributes?.title || '';
    const slug = createSlug(title);
    const to = createResourceLocatorString('ListingPage', routeConfiguration, { id, slug }, {});
    setIsOpen(false);
    setSuggestions([]);
    history.push(to);
  };

  const isKeywordsSearch = isMainSearchTypeKeywords(config);
  const initialValues = useMemo(() => getInitialValues(config, location.search), [
    config,
    location.search,
  ]);

  const submitSearch = values => {
    const searchParams = {
      ...currentSearchParams,
      ...getSearchParams(config, values),
    };
    // An emptied keyword search is dropped from the URL rather than sent as an empty value.
    if (!searchParams.keywords) {
      delete searchParams.keywords;
    }
    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
    // blur search input to hide software keyboard
    inputRef?.current?.blur();
  };

  // Empties the input. If the results are filtered by a keyword, that search is cleared too
  // (other filters stay), so the results don't stay filtered by text that's no longer shown.
  const handleClear = form => {
    form.change('keywords', undefined);
    setSuggestions([]);
    setIsOpen(false);
    if (currentSearchParams?.keywords) {
      submitSearch({ keywords: undefined });
    } else {
      inputRef?.current?.focus();
    }
  };

  const placeholder = intl.formatMessage({ id: 'TopbarSearchForm.placeholder' });
  const searchLabel = intl.formatMessage({ id: 'TopbarDesktop.screenreader.search' });
  const clearLabel = intl.formatMessage({ id: 'SearchPageSearchBar.clear' });

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={values =>
        isKeywordsSearch
          ? submitSearch({ keywords: values.keywords })
          : submitSearch({ location: values.location })
      }
      render={({ handleSubmit, form, values }) => (
        <Form
          className={classNames(css.root, className)}
          onSubmit={handleSubmit}
          enforcePagePreloadFor="SearchPage"
        >
          {isKeywordsSearch ? (
            <div className={css.inputWrapper} ref={wrapperRef} style={{ position: 'relative' }}>
              <Field
                name="keywords"
                render={({ input }) => (
                  <input
                    {...input}
                    onChange={event => handleKeywordsChange(event.target.value, input.onChange)}
                    onFocus={event => {
                      input.onFocus(event);
                      if (input.value && suggestions.length > 0) {
                        setIsOpen(true);
                      }
                    }}
                    ref={inputRef}
                    id="search-page-keyword-search"
                    className={values.keywords ? css.inputWithClear : css.input}
                    type="text"
                    enterKeyHint="search"
                    placeholder={placeholder}
                    aria-label={searchLabel}
                    autoComplete="off"
                  />
                )}
              />
              {values.keywords ? (
                <button
                  type="button"
                  className={css.clear}
                  aria-label={clearLabel}
                  onClick={() => handleClear(form)}
                >
                  <IconClose rootClassName={css.clearIcon} />
                </button>
              ) : null}
              <button type="submit" className={css.submit} aria-label={searchLabel}>
                <IconSearchDesktop />
              </button>
              {isOpen ? (
                <div className={dropdownCss.dropdown}>
                  {isLoading ? (
                    <div className={dropdownCss.loading}>Searching…</div>
                  ) : suggestions.length > 0 ? (
                    suggestions.map(listing => (
                      <button
                        key={listing.id.uuid}
                        type="button"
                        className={dropdownCss.item}
                        onMouseDown={e => {
                          // onMouseDown (not onClick) fires before the input's blur,
                          // so the dropdown doesn't close before the click registers.
                          e.preventDefault();
                          handleSelectListing(listing);
                        }}
                      >
                        {listing.attributes.title}
                      </button>
                    ))
                  ) : (
                    <div className={dropdownCss.empty}>No matching listings</div>
                  )}
                </div>
              ) : null}
            </div>
          ) : (
            <Field
              name="location"
              format={identity}
              render={({ input, meta }) => {
                const { onChange, ...restInput } = input;

                // Search as soon as a place is picked, like the topbar search does.
                const searchOnChange = value => {
                  onChange(value);
                  if (value?.selectedPlace) {
                    submitSearch({ location: value });
                  }
                };

                return (
                  <LocationAutocompleteInput
                    id="search-page-location-search"
                    className={css.locationRoot}
                    iconClassName={css.locationIcon}
                    inputClassName={css.locationInput}
                    predictionsClassName={css.predictions}
                    placeholder={placeholder}
                    inputRef={inputRef}
                    input={{ ...restInput, onChange: searchOnChange }}
                    meta={meta}
                    ariaLabel={searchLabel}
                  />
                );
              }}
            />
          )}
        </Form>
      )}
    />
  );
};

export default SearchPageSearchBar;
